/**
 * API client for the AI that places calls itself ("AI helistab", Settings ->
 * Phone -> AI calls out), the company card's "AI test call" button, and the
 * browser's mirror of the settings behind both.
 *
 * Its own file rather than more of `telephonyAgent.ts`: the answering AI's
 * settings travel inside the telephony row, while these live in a Setting row
 * of their own (`crm.calls.aiOutbound`) with their own routes, and the button
 * on the company card must not pull the whole telephony client in behind it.
 *
 * A HAND-WRITTEN MIRROR of `apps/api/src/modules/crm-calls/lib/outbound/settings.ts`
 * and `readiness.ts`. `aiOutboundNotes.test.ts` (in the settings folder) reads
 * those files and holds the lists and bounds below against them, so a blocker
 * or a bound changed on the server fails a test here instead of rendering as a
 * raw key or a form the server answers with 400.
 *
 * React-free on purpose: the gate, the error readers and the status cache are
 * the parts worth testing, and `aiOutbound.test.ts` runs them under plain node.
 * The test call's error codes and their reader live in `aiTestCallErrors.ts`
 * (re-exported below).
 */
import { ApiError, api } from "./api";
import type { BusinessDay } from "./telephonyHours";

// ---------------------------------------------------------------- mirror

/**
 * Everything that stops the AI calling out, in the order somebody would fix
 * them. Mirrors `OUTBOUND_BLOCKERS`.
 */
export const OUTBOUND_BLOCKERS = [
  "disabled",
  "no_twilio",
  "no_public_url",
  "no_elevenlabs_key",
  "no_voice",
  "no_gemini_key",
  "no_test_numbers",
  "no_caller_id",
] as const;
export type OutboundBlocker = (typeof OUTBOUND_BLOCKERS)[number];

/** The PUT schema refuses more test numbers than this, rather than clamping. */
export const OUTBOUND_MAX_TEST_NUMBERS = 5;
/** The bounds the server clamps each number setting inside. */
export const OUTBOUND_DAILY_CAP = { min: 1, max: 500, fallback: 20 } as const;
export const OUTBOUND_TEST_DAILY_CAP = { min: 1, max: 50, fallback: 10 } as const;
export const OUTBOUND_MIN_GAP_SEC = { min: 30, max: 3600, fallback: 120 } as const;
export const OUTBOUND_RING_SECONDS = { min: 10, max: 60, fallback: 25 } as const;
export const OUTBOUND_MAX_CALL_SEC = { min: 60, max: 600, fallback: 240 } as const;
export const OUTBOUND_COOLDOWN_DAYS = { min: 1, max: 365, fallback: 30 } as const;
export const OUTBOUND_RECORDING_DAYS = { min: 7, max: 3650, fallback: 90 } as const;
export const OUTBOUND_TRANSCRIPT_DAYS = { min: 7, max: 3650, fallback: 365 } as const;

/**
 * Longest reason sentence, question and offer, and how many extra reason
 * sentences one slot holds. Mirrors `OUTBOUND_TEXT_LIMITS` (`settings-texts.ts`).
 */
export const OUTBOUND_TEXT_LIMITS = { reason: 300, question: 200, pitch: 600, extraReasons: 4 } as const;
/** The opening's reason slots, by the contact that reached the company. Mirrors `OPENING_REASON_SLOTS`. */
export const OPENING_REASON_SLOTS = ["letter", "call", "cold"] as const;
export type OpeningReasonSlot = (typeof OPENING_REASON_SLOTS)[number];
/** What a call is for. Mirrors `OUTBOUND_GOALS` (`lib/agent/outbound-types.ts`). */
export const OUTBOUND_GOALS = ["meeting", "callback", "qualify", "send_report"] as const;
export type OutboundGoal = (typeof OUTBOUND_GOALS)[number];

/** The stored row. Mirrors `AiOutboundSettings`; it holds no secrets. */
export interface AiOutboundSettings {
  /** AI may call out at all (the button and the automation). Also the stop switch. */
  enabled: boolean;
  /** The automation works the active campaigns by itself (R4). Off by default. */
  autopilot: boolean;
  /** Every AI call rings a test number. Ships on. */
  testMode: boolean;
  /** E.164, at most `OUTBOUND_MAX_TEST_NUMBERS`. */
  testNumbers: string[];
  /** An owned Twilio number in E.164, or "" for the account's main number. */
  fromNumber: string;
  dailyCap: number;
  testDailyCap: number;
  /** The calling window: always seven, index 0 is Sunday. A day whose end is not after its start is closed. */
  days: BusinessDay[];
  timezone: string;
  skipHolidays: boolean;
  minGapSec: number;
  ringSeconds: number;
  maxCallSec: number;
  machineDetection: boolean;
  cooldownDays: number;
  recordingDays: number;
  transcriptDays: number;
  /** The opening's reason sentence per slot; "" is the built-in one. */
  openingReasons: Record<OpeningReasonSlot, string>;
  /**
   * More reason sentences per slot, said in turn with the first one: each AI
   * call to a company takes the next. Absent from a server older than this field.
   */
  extraOpeningReasons?: Record<OpeningReasonSlot, string[]>;
  /** The opening's last question; "" is the built-in one. */
  openingQuestion: string;
  /** What we offer, background for the AI; "" is none. */
  pitch: string;
  /** What a test call is for when the button does not say. */
  goal: OutboundGoal;
  /**
   * An AI alert call's defect report letter goes to the company, sent by the AI
   * (`settings-report-mail.ts`). Ships off: the letter goes to the test inbox.
   * Absent from a server older than the field, which reads as off.
   */
  reportMailLive?: boolean;
  /** Where the AI's letter goes while that is off, and every test call's letter. "" or absent sends nothing. */
  reportMailTestInbox?: string;
}

/**
 * What the settings form may send: the R1 fields, and the automation's switch,
 * calling window, zone, holidays and gap (R4). The retention ages are stated,
 * never edited, and deliberately absent, so no form can reset them.
 */
export type AiOutboundSettingsPatch = Partial<
  Pick<
    AiOutboundSettings,
    | "enabled"
    | "autopilot"
    | "testMode"
    | "testNumbers"
    | "fromNumber"
    | "dailyCap"
    | "testDailyCap"
    | "days"
    | "timezone"
    | "skipHolidays"
    | "minGapSec"
    | "ringSeconds"
    | "maxCallSec"
    | "machineDetection"
    | "cooldownDays"
    | "reportMailLive"
    | "reportMailTestInbox"
  >
> &
  /** The opening and offer editor's fields; the reason slots may go one at a time. */
  Partial<Pick<AiOutboundSettings, "openingQuestion" | "pitch" | "goal">> & {
    openingReasons?: Partial<Record<OpeningReasonSlot, string>>;
    /** A slot sent is its whole list. */
    extraOpeningReasons?: Partial<Record<OpeningReasonSlot, string[]>>;
  };

/** GET and PUT answer with this. */
export interface AiOutboundStatus {
  settings: AiOutboundSettings;
  /** `blockers` is empty: automation could call. */
  ready: boolean;
  /** What stops a live call, in the server's order. */
  blockers: OutboundBlocker[];
  /** What stops the company card's test call (it always needs a test number). */
  testCallBlockers: OutboundBlocker[];
  /** The account caller id the AI uses while `fromNumber` is "", or "" when unset. */
  mainNumber: string;
  /** Numbers bought on the Twilio account; null unless asked for, or when Twilio gave no complete list. */
  ownedNumbers: string[] | null;
  /** The built-in reason sentences and question, shown where the owner's own are blank. */
  openingDefaults: { question: string; reasons: Record<OpeningReasonSlot, string> };
}

/**
 * Why a real call to this company would be refused: the test-call route's
 * `realCall.blockers` codes. Not held by the mirror test, because the server
 * keeps them as a type union rather than a list a test could read; an unknown
 * code is worded as "unknown" instead (`realCallSkip`).
 */
export const REAL_CALL_BLOCKERS = [
  "no_phone",
  "invalid_phone",
  "mobile_excluded",
  "suppressed",
  "foreign_phone",
  "sole_trader",
  "deleted_company",
] as const;

/** What the test call found about the company's real number: the route's `notes` codes, same caveat. */
export const TEST_CALL_NOTES = ["target_suppressed", "target_unreachable", "no_target_phone"] as const;

/** A placed test call. */
export interface AiOutboundTestCall {
  callLogId: string;
  /** The test number that rings. */
  toNumber: string;
  /** The company's real register number. */
  intendedNumber: string | null;
  test: true;
  openingText: string;
  goal: string;
  /** Strings, so a newer server's code still reaches the toast. */
  notes: string[];
  realCall: {
    phone: string | null;
    legalState: "register_company" | "register_personal";
    blockers: string[];
  };
}

// ---------------------------------------------------------------- requests

const SETTINGS_PATH = "/admin/crm/calls/ai-outbound/settings";

/**
 * The settings and the server's verdict. `withNumbers` also asks Twilio for the
 * owned numbers (a round trip), so only the settings tab sets it.
 */
export function getAiOutboundStatus(opts?: { withNumbers?: boolean }): Promise<AiOutboundStatus> {
  return api.get(`${SETTINGS_PATH}${opts?.withNumbers ? "?numbers=1" : ""}`);
}

/** A merge patch: whatever is left out keeps its stored value. */
export function saveAiOutboundSettings(patch: AiOutboundSettingsPatch): Promise<AiOutboundStatus> {
  return api.put(SETTINGS_PATH, patch);
}

/**
 * Rings a test number with this company as the conversation: named by register
 * code, domain, or both, and with a campaign's words and call flow map when one is named.
 */
export function placeAiOutboundTestCall(body: { regCode?: string; domain?: string; campaignId?: string }): Promise<AiOutboundTestCall> {
  return api.post("/admin/crm/calls/ai-outbound/test-call", body);
}

// ---------------------------------------------------------------- status cache

/** How long the company card trusts a status it already fetched. */
export const STATUS_TTL_MS = 60_000;

export interface StatusCache<T> {
  get(now?: number): Promise<T>;
  invalidate(): void;
}

/**
 * A single cached value with a time limit.
 *
 * The company page opens dozens of times a session and every open would
 * otherwise ask the server again. A request in flight is shared, and a failed
 * one is dropped at once, so an error is never served from the cache.
 */
export function createStatusCache<T>(load: () => Promise<T>, ttlMs: number = STATUS_TTL_MS): StatusCache<T> {
  let entry: { at: number; promise: Promise<T> } | null = null;
  return {
    get(now = Date.now()) {
      if (entry && now - entry.at < ttlMs) return entry.promise;
      const current = { at: now, promise: load() };
      entry = current;
      current.promise.catch(() => {
        if (entry === current) entry = null;
      });
      return current.promise;
    },
    invalidate() {
      entry = null;
    },
  };
}

const statusCache = createStatusCache(() => getAiOutboundStatus());

/** The status without the Twilio list, cached for `STATUS_TTL_MS`. */
export function getAiOutboundStatusCached(now?: number): Promise<AiOutboundStatus> {
  return statusCache.get(now);
}

/** Called after a save and after a refusal that says the cached verdict was stale. */
export function invalidateAiOutboundStatus(): void {
  statusCache.invalidate();
}

// ---------------------------------------------------------------- gate

export type TestCallGate = { ok: true } | { ok: false; reason: string; code: string };

const KNOWN_BLOCKERS: ReadonlySet<string> = new Set<string>(OUTBOUND_BLOCKERS);

/**
 * May the company card's button be pressed, and if not, why.
 *
 * The server's own `testCallBlockers` decide, never a re-derivation here: that
 * list already adds the missing test number even with test mode off, which is
 * the one place the button and automation disagree. `reason` is a suffix under
 * `aiOutbound.testCall.reason`; a code this build does not know blocks as
 * "unknown" and carries the code, so it is still printed.
 */
export function testCallGate(status: AiOutboundStatus | null): TestCallGate {
  if (!status) return { ok: false, reason: "load_failed", code: "load_failed" };
  const first = status.testCallBlockers?.[0];
  if (first === undefined) return { ok: true };
  return { ok: false, reason: KNOWN_BLOCKERS.has(first) ? first : "unknown", code: first };
}

// ---------------------------------------------------------------- test call answer

export interface RealCallSkip {
  /** The server's code, verbatim. */
  code: string;
  /** Suffix under `aiOutbound.testCall.skip`: the code itself, or "unknown". */
  key: string;
}

const KNOWN_SKIPS: ReadonlySet<string> = new Set<string>([...REAL_CALL_BLOCKERS, ...TEST_CALL_NOTES]);

/**
 * The first reason a real call to this company would not happen: a refusal of
 * the register number first, then a note about it. Null when a real call would
 * go ahead.
 */
export function realCallSkip(res: Pick<AiOutboundTestCall, "notes" | "realCall">): RealCallSkip | null {
  const code = res.realCall?.blockers?.[0] ?? res.notes?.[0];
  if (!code) return null;
  return { code, key: KNOWN_SKIPS.has(code) ? code : "unknown" };
}

// ---------------------------------------------------------------- test call errors

// Moved to their own file (`aiTestCallErrors.ts`); re-exported so existing imports keep working.
export { TEST_CALL_ERRORS, testCallError, type TestCallError } from "./aiTestCallErrors";

// ---------------------------------------------------------------- settings save errors

/** Every refusal the settings PUT names. */
export const SETTINGS_SAVE_ERRORS = [
  "invalid_body",
  "invalid_number",
  "caller_not_owned",
  "own_line",
  "suppressed",
  "caller_ids_unavailable",
  "telephony_unconfigured",
  "opening_invalid",
] as const;
const KNOWN_SAVE_ERRORS: ReadonlySet<string> = new Set<string>(SETTINGS_SAVE_ERRORS);

export interface SettingsSaveError {
  /**
   * Suffix under `settings.aiOutbound.error`: a known code, "forbidden" or
   * "failed". "telephony_unconfigured" refused on the test numbers is
   * "telephony_unconfigured_tests": its sentence is about a different field.
   */
  key: string;
  /** Which field the server refused, when it said. */
  field: "fromNumber" | "testNumbers" | null;
  /** The refused value, or the refused field names of a 400; "" when none. */
  value: string;
  /** On `opening_invalid`: the text field ("openingReasons.cold", "openingQuestion") and what its opening lacks. */
  opening: { field: string; problems: string[] } | null;
}

/** A thrown save error as one note. */
export function settingsSaveError(err: unknown): SettingsSaveError {
  const out: SettingsSaveError = { key: "failed", field: null, value: "", opening: null };
  if (!(err instanceof ApiError)) return out;
  if (err.status === 403) return { ...out, key: "forbidden" };
  const body = (err.body ?? {}) as { error?: unknown; field?: unknown; value?: unknown; fields?: unknown; problems?: unknown };
  const code = typeof body.error === "string" ? body.error : "";
  if (!KNOWN_SAVE_ERRORS.has(code)) return out;
  if (code === "opening_invalid") {
    const problems = Array.isArray(body.problems) ? body.problems.filter((p): p is string => typeof p === "string") : [];
    return { ...out, key: code, opening: { field: typeof body.field === "string" ? body.field : "", problems } };
  }
  const { field: rawField, value: rawValue, fields: rawFields } = body;
  const field = rawField === "fromNumber" || rawField === "testNumbers" ? rawField : null;
  const fields = Array.isArray(rawFields) ? rawFields.filter((f): f is string => typeof f === "string") : [];
  const value = typeof rawValue === "string" ? rawValue : fields.join(", ");
  const key = code === "telephony_unconfigured" && field === "testNumbers" ? "telephony_unconfigured_tests" : code;
  return { key, field, value, opening: null };
}
